import React, { useEffect, useState } from 'react';
import { Gamepad2, Lock, Unlock } from 'lucide-react';
import type { OnlineAccessConfig } from '../types';
import {
  fetchOnlineAccessConfig,
  sanitizeOnlineAccessConfig,
  saveOnlineAccessConfig
} from '../storage';

export const ONLINE_ACCESS_UPDATED_EVENT = 'kendama-online-access-updated';

const AdminOnlineSettings: React.FC = () => {
  const [config, setConfig] = useState<OnlineAccessConfig>(() => sanitizeOnlineAccessConfig(null));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;

    const loadConfig = async () => {
      try {
        setLoading(true);
        setError('');
        const remoteConfig = await fetchOnlineAccessConfig();
        if (!mounted) return;
        setConfig(sanitizeOnlineAccessConfig(remoteConfig));
      } catch (err) {
        if (!mounted) return;
        setError((err as Error).message || '加载联机模式设置失败');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    void loadConfig();

    return () => {
      mounted = false;
    };
  }, []);

  const handleToggle = async () => {
    if (saving) return;
    const nextConfig: OnlineAccessConfig = {
      adminOnly: !config.adminOnly,
      updatedAt: Date.now()
    };

    try {
      setSaving(true);
      setMessage('');
      setError('');
      const savedConfig = await saveOnlineAccessConfig(nextConfig);
      setConfig(savedConfig);
      window.dispatchEvent(new Event(ONLINE_ACCESS_UPDATED_EVENT));
      setMessage(
        savedConfig.adminOnly
          ? '已开启：只有管理员可以进入联机模式。'
          : '已关闭：所有登录用户都可以进入联机模式。'
      );
    } catch (err) {
      setError((err as Error).message || '保存联机模式设置失败');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="py-12 text-center font-orbitron text-zinc-500 animate-pulse">LOADING ONLINE SETTINGS...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-zinc-700 bg-zinc-900/60 p-4 md:p-6">
        <div className="flex items-start gap-3">
          <div className="rounded-full border border-blue-500/30 bg-blue-500/10 p-2">
            <Gamepad2 size={20} className="text-blue-300" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-orbitron text-sm md:text-base uppercase tracking-[0.14em] text-zinc-100">
              联机模式访问权限 / Online Mode Access
            </div>
            <p className="mt-2 text-xs md:text-sm text-zinc-400 font-rajdhani leading-6">
              开启后只有管理员可以进入联机模式（大厅、房间、对战全部接口都会校验），
              普通玩家点击首页「联机」会看到提示。
            </p>

            <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-zinc-800 bg-black/40 px-4 py-3">
              <div className="flex items-center gap-2">
                {config.adminOnly ? (
                  <Lock size={16} className="text-amber-300" />
                ) : (
                  <Unlock size={16} className="text-emerald-300" />
                )}
                <span className="font-orbitron text-xs md:text-sm text-zinc-200">
                  {config.adminOnly ? '仅管理员可进入' : '所有登录用户可进入'}
                </span>
              </div>

              <button
                type="button"
                onClick={handleToggle}
                disabled={saving}
                aria-pressed={config.adminOnly}
                className={`relative h-8 w-16 shrink-0 rounded-full border transition-colors disabled:opacity-60 ${
                  config.adminOnly
                    ? 'border-amber-400/60 bg-amber-500/70'
                    : 'border-zinc-600 bg-zinc-700/70'
                }`}
              >
                <span
                  className={`absolute top-1 h-6 w-6 rounded-full bg-white shadow transition-all ${
                    config.adminOnly ? 'left-9' : 'left-1'
                  }`}
                />
              </button>
            </div>

            {saving && (
              <div className="mt-3 text-xs text-zinc-400 font-rajdhani">正在保存…</div>
            )}
            {message && (
              <div className="mt-3 text-xs text-emerald-300 font-rajdhani">{message}</div>
            )}
            {error && (
              <div className="mt-3 text-xs text-red-400 font-rajdhani">{error}</div>
            )}
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-zinc-800 bg-black/30 p-4 text-xs text-zinc-500 font-rajdhani leading-6">
        提示：开关立即生效，玩家端在打开 App / 回到前台时会重新读取。
      </div>
    </div>
  );
};

export default AdminOnlineSettings;
